/** Page-global handle the analytics gate installs on `window`. */
export type AnalyticsGate = {
  /**
   * Runs `callback` now, or, while released analytics scripts are still
   * loading, after the deferred history updates are applied. Returns a
   * function that drops the callback if it has not run yet.
   */
  whenReady(callback: () => void): () => void;
  /** The URL of the page, including history updates that are still deferred. */
  href(): string;
};

declare global {
  interface Window {
    __analyticsGate?: AnalyticsGate;
  }
}

type AnalyticsGateConfig = {
  src: string[];
  eagerPath: string;
  eagerQuery: string;
  settleMs: number;
  maxHistoryDeferMs: number;
};

/** Analytics and tag-manager script URLs that wait for the first user input. Matched case-insensitively. */
export const GATED_ANALYTICS_SRC: RegExp[] = [
  /^(?:https?:)?\/\/(?:[\w-]+\.)*googletagmanager\.com\//i,
  /^(?:https?:)?\/\/(?:[\w-]+\.)*google-analytics\.com\//i,
  /^(?:https?:)?\/\/(?:[\w-]+\.)*connect\.facebook\.net\//i,
  /^(?:https?:)?\/\/(?:[\w-]+\.)*cdn\.dhq\.technology\//i,
  /^(?:https?:)?\/\/(?:[\w-]+\.)*bat\.bing\.com\//i,
  /^(?:https?:)?\/\/(?:[\w-]+\.)*clarity\.ms\//i,
  /^(?:https?:)?\/\/www\.floorlytics\.broadlu\.me\/ff8\/integrations-/i,
];

/** Whether a script URL is an analytics library that the gate holds back. */
export const isGatedAnalyticsSrc = (src: string): boolean =>
  GATED_ANALYTICS_SRC.some((pattern) => pattern.test(src));

/**
 * Inline script that inserts an external script with the given attributes.
 * The browser parser inserts a `<script src>` without `appendChild`, so the
 * gate can hold back only a script that another script inserts. A
 * `next/script` with a `src` would also emit a `<link rel="preload">` that
 * downloads the library before the release.
 */
export const gatedScriptLoader = (attribs: Record<string, string>): string =>
  `(function(a){var s=document.createElement('script');for(var k in a)s.setAttribute(k,a[k]);if(!('async' in a))s.async=false;document.head.appendChild(s)})(${JSON.stringify(
    attribs
  ).replace(/</g, '\\u003c')});`;

/**
 * Holds back the download of analytics libraries until the visitor's first
 * input, so they do not compete with hydration for the main thread. The inline
 * loader snippets still run, so `dataLayer`, `fbq` and `analytics` queue every
 * call made before the release.
 *
 * Pages that a visitor often leaves without input (thank-you pages) and
 * campaign landings release at once. After the release, the URL of a
 * navigation waits until the page and the libraries have loaded: GA, Meta and
 * Mosaic read `location` when they send the landing page view, and a menu tap
 * is often the first input. The history entry itself is created at once, so
 * Back still works; it carries its URL in its state, and a `popstate` restores
 * that URL before Next reads it. Code that reads the URL in that window uses
 * `href()`.
 *
 * Serialized into an inline `<head>` script, so it must not reference anything
 * outside its own body.
 */
export function installAnalyticsGate(
  win: Window & typeof globalThis,
  config: AnalyticsGateConfig
): void {
  type HistoryArgs = Parameters<History['pushState']>;
  const urlKey = '__analyticsGateUrl';
  // History updates and route-change callbacks, in the order they happened,
  // so each callback runs while `location` holds the URL of its own route.
  const pending: Array<
    ['pushState' | 'replaceState', HistoryArgs] | ['callback', () => void]
  > = [];
  let released =
    new RegExp(config.eagerPath, 'i').test(win.location.pathname) ||
    new RegExp(config.eagerQuery, 'i').test(win.location.search);
  let settled = released;
  win.__analyticsGate = {
    whenReady(callback) {
      if (!released || settled) {
        callback();
        return () => {};
      }
      const entry: ['callback', () => void] = ['callback', callback];
      pending.push(entry);
      return () => {
        const index = pending.indexOf(entry);
        if (index !== -1) pending.splice(index, 1);
      };
    },
    href() {
      let href = win.location.href;
      for (const [kind, args] of pending) {
        const url = kind === 'callback' ? null : args[2];
        if (url != null) href = new URL(String(url), href).href;
      }
      return href;
    },
  };
  if (released) return;

  const gatedSrc = config.src.map((source) => new RegExp(source, 'i'));
  const proto = win.Node.prototype;
  const nativeAppendChild = proto.appendChild;
  const nativeInsertBefore = proto.insertBefore;
  const nativePushState = win.history.pushState;
  const nativeReplaceState = win.history.replaceState;
  const held: Array<[Node, HTMLScriptElement, Node | null]> = [];
  let loading = 0;
  let replaying = false;
  let settleTimer: number | undefined;
  const releaseEvents = [
    'pointerdown',
    'keydown',
    'touchstart',
    'scroll',
    'wheel',
    'mousemove',
    'pagehide',
  ];

  const isGated = (node: Node): node is HTMLScriptElement =>
    node instanceof win.HTMLScriptElement &&
    gatedSrc.some((pattern) => pattern.test(node.getAttribute('src') ?? ''));

  const appendChild = function appendChild<T extends Node>(
    this: Node,
    node: T
  ): T {
    if (isGated(node)) {
      if (!released) {
        held.push([this, node, null]);
        return node;
      }
      track(node);
    }
    return nativeAppendChild.call(this, node) as T;
  };

  const insertBefore = function insertBefore<T extends Node>(
    this: Node,
    node: T,
    child: Node | null
  ): T {
    if (isGated(node)) {
      if (!released) {
        held.push([this, node, child]);
        return node;
      }
      track(node);
    }
    return nativeInsertBefore.call(this, node, child) as T;
  };

  const settle = () => {
    if (settled) return;
    settled = true;
    win.clearTimeout(settleTimer);
    // A script that patched these after the gate stays in place.
    if (proto.appendChild === appendChild)
      proto.appendChild = nativeAppendChild;
    if (proto.insertBefore === insertBefore) {
      proto.insertBefore = nativeInsertBefore;
    }
    for (const [kind, args] of pending.splice(0)) {
      if (kind === 'callback') {
        try {
          args();
        } catch (error) {
          win.setTimeout(() => {
            throw error;
          });
        }
        continue;
      }
      // Replays through the current top of the wrapper chain, so a tag
      // manager that patched history after it loaded sees the navigation.
      replaying = true;
      try {
        win.history[kind](...args);
      } finally {
        replaying = false;
      }
    }
  };

  const scheduleSettle = () => {
    win.clearTimeout(settleTimer);
    // Next appends `next/script` tags after hydration, which can end after
    // the release; the settle delay starts only once the page has loaded.
    if (loading === 0 && win.document.readyState === 'complete') {
      settleTimer = win.setTimeout(settle, config.settleMs);
    }
  };

  const track = (script: HTMLScriptElement) => {
    if (settled) return;
    loading++;
    win.clearTimeout(settleTimer);
    const done = () => {
      loading--;
      scheduleSettle();
    };
    script.addEventListener('load', done);
    script.addEventListener('error', done);
  };

  proto.appendChild = appendChild;
  proto.insertBefore = insertBefore;

  for (const method of ['pushState', 'replaceState'] as const) {
    win.history[method] = function (this: History, ...args: HistoryArgs) {
      // The entry already exists; the replay only gives it its URL.
      if (replaying) return nativeReplaceState.apply(this, args);
      if (!released || settled) {
        return (
          method === 'pushState' ? nativePushState : nativeReplaceState
        ).apply(this, args);
      }
      pending.push([method, args]);
      const state = args[0];
      const marked =
        state != null && typeof state === 'object'
          ? Object.assign({}, state, { [urlKey]: win.__analyticsGate?.href() })
          : state;
      (method === 'pushState' ? nativePushState : nativeReplaceState).call(
        this,
        marked,
        args[1]
      );
    };
  }

  // Registered before Next's own listener, so Next reads the restored URL.
  win.addEventListener('popstate', () => {
    if (released && !settled) {
      // The deferred work belongs to entries the visitor just left.
      pending.length = 0;
      settle();
    }
    const state = win.history.state;
    const url = state != null && typeof state === 'object' && state[urlKey];
    if (typeof url !== 'string') return;
    const clean = Object.assign({}, state);
    delete clean[urlKey];
    nativeReplaceState.call(win.history, clean, '', url);
  });

  const release = () => {
    if (released) return;
    released = true;
    for (const type of releaseEvents) {
      win.removeEventListener(type, release, true);
    }
    win.document.removeEventListener('visibilitychange', onVisibilityChange);
    for (const [parent, script, child] of held.splice(0)) {
      if (child?.parentNode === parent) parent.insertBefore(script, child);
      else
        (parent.isConnected ? parent : win.document.head).appendChild(script);
    }
    scheduleSettle();
    win.setTimeout(settle, config.maxHistoryDeferMs);
  };

  const onVisibilityChange = () => {
    if (win.document.visibilityState === 'hidden') release();
  };

  for (const type of releaseEvents) {
    win.addEventListener(type, release, { capture: true, passive: true });
  }
  win.document.addEventListener('visibilitychange', onVisibilityChange);
  win.addEventListener('load', () => {
    if (released) scheduleSettle();
  });
}

/** Inline `<head>` script that installs the analytics gate. Must run before any CMS script. */
export const ANALYTICS_GATE_SCRIPT = `(${installAnalyticsGate.toString()})(window, ${JSON.stringify(
  {
    src: GATED_ANALYTICS_SRC.map((pattern) => pattern.source),
    eagerPath: 'thank|confirm|success',
    eagerQuery:
      '[?&](?:gclid|gbraid|wbraid|fbclid|msclkid|ttclid|dclid|_gl|utm_[a-z]+)=',
    settleMs: 600,
    maxHistoryDeferMs: 4000,
  } satisfies AnalyticsGateConfig
)});`;

/**
 * Runs `callback` once the URL matches the rendered route. While released
 * analytics scripts load, the gate defers history updates, so route-change
 * work that reads `location` must wait for it. Returns a function that drops
 * the callback if it has not run yet.
 */
export function afterAnalyticsGate(callback: () => void): () => void {
  const gate =
    typeof window === 'undefined' ? undefined : window.__analyticsGate;
  if (gate) return gate.whenReady(callback);
  callback();
  return () => {};
}

/** The URL of the rendered route. `window.location` lags behind it while the gate defers history updates. */
export function currentUrl(): URL {
  return new URL(window.__analyticsGate?.href() ?? window.location.href);
}
